import React, { useState, useEffect } from "react";
import { getInstaManage, setHiddenReels } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Eye, EyeOff, Play, CheckSquare, Square, RefreshCw } from "lucide-react";

export default function InstaReelsManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [hidden, setHidden] = useState(new Set());
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getInstaManage()
      .then((res) => {
        setConfigured(res.configured);
        setItems(res.data || []);
        setHidden(new Set((res.data || []).filter((i) => i.hidden).map((i) => i.id)));
      })
      .catch(() => toast({ title: "Could not load reels" }))
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = async (nextHidden) => {
    setSaving(true);
    try {
      await setHiddenReels([...nextHidden]);
    } catch (e) {
      toast({ title: "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const toggleOne = (id) => {
    const next = new Set(hidden);
    next.has(id) ? next.delete(id) : next.add(id);
    setHidden(next);
    persist(next);
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const bulk = (hide) => {
    if (!selected.size) { toast({ title: "Select some reels first" }); return; }
    const next = new Set(hidden);
    selected.forEach((id) => (hide ? next.add(id) : next.delete(id)));
    setHidden(next);
    setSelected(new Set());
    persist(next);
    toast({ title: hide ? "Hidden from website" : "Shown on website", description: `${selected.size} reel(s) updated` });
  };

  const visibleCount = items.length - hidden.size;

  if (loading) return <div className="text-cream/60 py-10 text-center">Loading your reels…</div>;

  if (!configured)
    return (
      <div className="p-8 rounded-2xl border hairline bg-viimii-2 text-center text-cream/70">
        Instagram isn't connected right now, so there are no live reels to manage.
      </div>
    );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="text-sm text-cream/60">
          <span className="text-gold" style={{ color: "var(--viimii-gold)" }}>{visibleCount}</span> showing · {hidden.size} hidden
          {saving && <span className="ml-2 text-cream/40">saving…</span>}
        </p>
        <div className="flex items-center gap-2">
          <button onClick={() => bulk(true)} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border hairline text-cream/80 hover:text-gold">
            <EyeOff size={15} /> Hide selected
          </button>
          <button onClick={() => bulk(false)} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full text-black" style={{ background: "var(--viimii-gold)" }}>
            <Eye size={15} /> Show selected
          </button>
          <button onClick={load} className="w-9 h-9 grid place-items-center rounded-full border hairline text-cream/70 hover:text-gold" title="Refresh">
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      <p className="text-xs text-cream/45 mb-5">Tap the eye on any reel to instantly show/hide it on the website. Use the checkbox to select multiple, then Hide/Show selected. Hidden reels won't appear in the hero or the Instagram section.</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
        {items.map((m) => {
          const isHidden = hidden.has(m.id);
          const isSel = selected.has(m.id);
          return (
            <div key={m.id} className={`relative rounded-2xl overflow-hidden border ${isSel ? "border-gold" : "hairline"} bg-black`} style={isSel ? { borderColor: "var(--viimii-gold)" } : {}}>
              <div className="aspect-[9/16] overflow-hidden">
                <img src={m.thumbnail_url || m.image_url} alt={m.caption || "reel"} className={`w-full h-full object-cover transition ${isHidden ? "opacity-35 grayscale" : ""}`} loading="lazy" />
              </div>
              <span className="absolute top-2 left-2 w-7 h-7 rounded-full bg-black/50 grid place-items-center">
                <Play size={11} className="text-white fill-white ml-0.5" />
              </span>
              {/* select checkbox */}
              <button onClick={() => toggleSelect(m.id)} className="absolute top-2 right-2 w-7 h-7 rounded-md grid place-items-center bg-black/50 text-white" title="Select">
                {isSel ? <CheckSquare size={16} /> : <Square size={16} />}
              </button>
              {/* one-tap eye toggle */}
              <button
                onClick={() => toggleOne(m.id)}
                className="absolute bottom-2 left-2 right-2 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium backdrop-blur-md transition"
                style={isHidden ? { background: "rgba(0,0,0,0.6)", color: "#fff" } : { background: "var(--viimii-gold)", color: "#000" }}
              >
                {isHidden ? <><EyeOff size={13} /> Hidden</> : <><Eye size={13} /> Showing</>}
              </button>
              {isHidden && <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-white/10" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
